<template>
    <div class="content mk-aff-settings">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">{{ $t("marketing.menu.affiliate_settings") }}</h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-breadcrumb></route-breadcrumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <el-alert v-if="schemaMissing" type="error" :closable="false" show-icon class="mb-3" :title="$t('marketing.schema_missing')" data-testid="schema-missing"></el-alert>
            <el-alert v-else-if="settings && settings.env_enabled === false" type="warning" :closable="false" show-icon class="mb-3" :title="$t('marketing.affiliate.env_off')" data-testid="aff-env-off"></el-alert>

            <card body-classes="pt-2">
                <el-tabs v-model="tab" data-testid="aff-settings-tabs">
                    <!-- Tab 1: program -->
                    <el-tab-pane :label="$t('marketing.affiliate.settings.tab_program')" name="program">
                        <div v-loading="loading" class="aff-form">
                            <el-alert v-if="stale" type="warning" :closable="false" show-icon class="mb-3" :title="$t('marketing.affiliate.settings.stale')">
                                <base-button size="sm" type="secondary" class="mt-1" @click="load">{{ $t("marketing.affiliate.reload") }}</base-button>
                            </el-alert>

                            <div class="form-group d-flex align-items-center" style="gap: 12px;">
                                <el-switch :value="form.enabled" :disabled="!canWrite || schemaMissing || saving" data-testid="aff-enabled" @change="onToggle"></el-switch>
                                <span class="font-weight-bold">{{ $t("marketing.affiliate.settings.enabled") }}</span>
                                <el-tag size="small" :type="form.enabled ? 'success' : 'info'">{{ form.enabled ? $t("marketing.affiliate.on") : $t("marketing.affiliate.off") }}</el-tag>
                            </div>

                            <div class="row">
                                <div class="col-md-4 form-group">
                                    <label class="form-control-label">{{ $t("marketing.affiliate.settings.click_window_days") }}</label>
                                    <el-input-number v-model="form.click_window_days" :min="1" :max="30" :step="1" step-strictly class="w-100" :disabled="!canWrite" data-testid="aff-window"></el-input-number>
                                    <small v-if="errors.click_window_days" class="text-danger">{{ $t("marketing.affiliate.err.range") }} (1–30)</small>
                                </div>
                                <div class="col-md-4 form-group">
                                    <label class="form-control-label">{{ $t("marketing.affiliate.settings.maturity_days") }}</label>
                                    <el-input-number v-model="form.maturity_days" :min="0" :max="90" :step="1" class="w-100" :disabled="!canWrite" data-testid="aff-maturity"></el-input-number>
                                    <small v-if="errors.maturity_days" class="text-danger">{{ $t("marketing.affiliate.err.range") }} (0–90)</small>
                                </div>
                                <div class="col-md-4 form-group">
                                    <label class="form-control-label">{{ $t("marketing.affiliate.settings.min_payout") }}</label>
                                    <el-input-number v-model="form.min_payout" :min="0" :precision="2" :step="50" class="w-100" :disabled="!canWrite" data-testid="aff-min-payout"></el-input-number>
                                    <small v-if="errors.min_payout" class="text-danger">{{ $t("marketing.affiliate.err.range") }}</small>
                                </div>
                            </div>

                            <div class="form-group">
                                <label class="form-control-label">{{ $t("marketing.affiliate.settings.wht") }}</label>
                                <div class="text-muted" data-testid="aff-wht">{{ form.wht_percent }}%</div>
                            </div>

                            <div class="row">
                                <div class="col-lg-6 form-group">
                                    <label class="form-control-label">{{ $t("marketing.affiliate.settings.terms_th") }}</label>
                                    <el-input v-model="form.terms_th" type="textarea" :rows="8" :disabled="!canWrite" data-testid="aff-terms-th"></el-input>
                                </div>
                                <div class="col-lg-6 form-group">
                                    <label class="form-control-label">{{ $t("marketing.affiliate.settings.terms_en") }}</label>
                                    <el-input v-model="form.terms_en" type="textarea" :rows="8" :disabled="!canWrite" data-testid="aff-terms-en"></el-input>
                                </div>
                            </div>
                            <div class="form-group">
                                <el-checkbox v-model="bumpTerms" :disabled="!canWrite" data-testid="aff-bump-terms">{{ $t("marketing.affiliate.settings.bump_terms") }}</el-checkbox>
                                <small class="d-block text-muted">{{ $t("marketing.affiliate.settings.terms_version", { v: settings ? settings.terms_version : 1 }) }}</small>
                            </div>

                            <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 8px;">
                                <small class="text-muted">
                                    <template v-if="settings && settings.updated_at">{{ $t("marketing.affiliate.updated_by", { name: settings.updated_by_name || "-", at: fmt(settings.updated_at) }) }}</template>
                                </small>
                                <base-button v-if="canWrite" type="primary" :loading="saving" :disabled="schemaMissing" data-testid="aff-save" @click="save()">{{ $t("marketing.affiliate.save") }}</base-button>
                            </div>
                        </div>
                    </el-tab-pane>

                    <!-- Tab 2: rates -->
                    <el-tab-pane :label="$t('marketing.affiliate.settings.tab_rates')" name="rates">
                        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3" style="gap: 8px;">
                            <el-select v-model="scopeFilter" size="small" clearable :placeholder="$t('marketing.affiliate.rule.all_scopes')" style="width: 200px;" data-testid="aff-rule-scope-filter">
                                <el-option v-for="s in scopes" :key="s" :value="s" :label="tk('marketing.affiliate.rule.scope_', s)"></el-option>
                            </el-select>
                            <base-button v-if="canWrite && !schemaMissing" type="primary" size="sm" data-testid="aff-rule-add" @click="openRule(null)">
                                <i class="fa fa-plus"></i> {{ $t("marketing.affiliate.rule.add") }}
                            </base-button>
                        </div>
                        <el-alert v-if="rulesLoaded && !hasDefault" type="warning" :closable="false" show-icon class="mb-3" :title="$t('marketing.affiliate.rule.no_default')" data-testid="aff-no-default"></el-alert>

                        <el-table v-loading="rulesLoading" :data="shownRules" class="aff-table bon-table bon-tight" header-row-class-name="thead-light" :empty-text="$t('marketing.affiliate.empty')" data-testid="aff-rule-table">
                            <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.rule.scope')" width="130">
                                <template v-slot="{ row }">{{ tk("marketing.affiliate.rule.scope_", row.scope) }}</template>
                            </el-table-column>
                            <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.rule.target')" min-width="180">
                                <template v-slot="{ row }">
                                    <span v-if="row.scope === 'default'" class="text-muted">{{ $t("marketing.affiliate.rule.all_products") }}</span>
                                    <span v-else>{{ row.target_name || "#" + row.scope_id }} <small class="text-muted">#{{ row.scope_id }}</small></span>
                                </template>
                            </el-table-column>
                            <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.rule.rate')" width="130">
                                <template v-slot="{ row }"><span class="font-weight-bold">{{ rate(row) }}</span></template>
                            </el-table-column>
                            <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.rule.window')" min-width="150">
                                <template v-slot="{ row }">
                                    <span v-if="!row.starts_at && !row.ends_at" class="text-muted small">{{ $t("marketing.affiliate.rule.always") }}</span>
                                    <template v-else>
                                        <div class="small text-nowrap">{{ row.starts_at ? fmt(row.starts_at) : "…" }} –</div>
                                        <div class="small text-nowrap">{{ row.ends_at ? fmt(row.ends_at) : "…" }}</div>
                                    </template>
                                </template>
                            </el-table-column>
                            <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.rule.state')" width="110">
                                <template v-slot="{ row }"><el-tag size="small" :type="stateTag(row.state)">{{ tk("marketing.affiliate.rule.state_", row.state) }}</el-tag></template>
                            </el-table-column>
                            <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.rule.updated_by')" min-width="140">
                                <template v-slot="{ row }">
                                    <div class="small">{{ row.updated_by_name || "-" }}</div>
                                    <div class="small text-muted">{{ fmt(row.updated_at) }}</div>
                                </template>
                            </el-table-column>
                            <el-table-column class-name="bon-tight" v-if="canWrite" :label="$t('marketing.affiliate.actions')" width="72" align="center" fixed="right">
                                <template v-slot="{ row }">
                                    <el-dropdown trigger="click" @command="(c) => onRuleAction(c, row)">
                                        <base-button size="sm" type="neutral" class="px-2" :aria-label="$t('marketing.affiliate.actions')"><i class="fa fa-ellipsis-v"></i></base-button>
                                        <el-dropdown-menu slot="dropdown">
                                            <el-dropdown-item command="edit">{{ $t("marketing.affiliate.edit") }}</el-dropdown-item>
                                            <el-dropdown-item v-if="row.active" command="disable" divided class="text-danger">{{ $t("marketing.affiliate.rule.disable") }}</el-dropdown-item>
                                            <el-dropdown-item v-else command="enable" divided>{{ $t("marketing.affiliate.rule.enable") }}</el-dropdown-item>
                                        </el-dropdown-menu>
                                    </el-dropdown>
                                </template>
                            </el-table-column>
                        </el-table>
                        <p class="small text-muted mt-2 mb-0">{{ $t("marketing.affiliate.rule.precedence") }}</p>
                    </el-tab-pane>
                </el-tabs>
            </card>
        </div>

        <affiliate-rule-dialog :visible.sync="ruleOpen" :rule="ruleEdit" @saved="loadRules"></affiliate-rule-dialog>
    </div>
</template>

<script>
import { Tabs, TabPane, Switch, InputNumber, Input, Checkbox, Alert, Tag, Table, TableColumn, Select, Option, Dropdown, DropdownMenu, DropdownItem, Loading } from "element-ui";
import { mapGetters } from "vuex";
import { AffiliateService } from "@/resource";
import { apiErrorCode, isSchemaMissing } from "@/utils/marketing";
import { RULE_SCOPES, hasActiveDefault, rateLabel, ruleStateTag, settingsErrors, settingsPayload } from "@/utils/affiliate";
import affiliateMixin from "./components/affiliateMixin";
import AffiliateRuleDialog from "./components/AffiliateRuleDialog.vue";

// การตลาด → Affiliate: ตั้งค่า (Phase 3 spec 2026-10-13 §5.2).
export default {
    name: "MarketingAffiliateSettings",
    directives: { loading: Loading.directive },
    components: {
        [Tabs.name]: Tabs,
        [TabPane.name]: TabPane,
        [Switch.name]: Switch,
        [InputNumber.name]: InputNumber,
        [Input.name]: Input,
        [Checkbox.name]: Checkbox,
        [Alert.name]: Alert,
        [Tag.name]: Tag,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Select.name]: Select,
        [Option.name]: Option,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem,
        AffiliateRuleDialog
    },
    mixins: [affiliateMixin],
    data() {
        return {
            tab: "program",
            loading: false,
            saving: false,
            stale: false,
            schemaMissing: false,
            settings: null,
            form: { enabled: false, click_window_days: 3, maturity_days: 7, min_payout: 300, wht_percent: 3, terms_th: "", terms_en: "", updated_at: null },
            bumpTerms: false,
            errors: {},
            scopes: RULE_SCOPES,
            scopeFilter: "",
            rules: [],
            rulesLoading: false,
            rulesLoaded: false,
            ruleOpen: false,
            ruleEdit: null
        };
    },
    computed: {
        ...mapGetters({ canWrite: "Auth/canWriteMarketing" }),
        shownRules() {
            return this.scopeFilter ? this.rules.filter((r) => r.scope === this.scopeFilter) : this.rules;
        },
        hasDefault() {
            return hasActiveDefault(this.rules);
        }
    },
    created() {
        this.load();
        this.loadRules();
    },
    methods: {
        stateTag: ruleStateTag,
        rate(row) {
            return rateLabel(row.rate_type, row.rate, this.$t("marketing.affiliate.rule.per_item"));
        },
        applySettings(s) {
            this.settings = s;
            this.form = {
                enabled: !!s.enabled,
                click_window_days: s.click_window_days,
                maturity_days: s.maturity_days,
                min_payout: s.min_payout,
                wht_percent: s.wht_percent,
                terms_th: s.terms_th || "",
                terms_en: s.terms_en || "",
                updated_at: s.updated_at || null
            };
            this.bumpTerms = false;
            this.stale = false;
        },
        async load() {
            this.loading = true;
            try {
                this.applySettings(this.unwrap(await AffiliateService.settings()) || {});
                this.schemaMissing = false;
            } catch (err) {
                if (isSchemaMissing(err)) this.schemaMissing = true;
                else this.showError(err);
            } finally {
                this.loading = false;
            }
        },
        async loadRules() {
            this.rulesLoading = true;
            try {
                const d = this.unwrap(await AffiliateService.rules({}));
                this.rules = Array.isArray(d) ? d : [];
            } catch (err) {
                this.rules = [];
                if (isSchemaMissing(err)) this.schemaMissing = true;
            } finally {
                this.rulesLoading = false;
                this.rulesLoaded = !this.schemaMissing;
            }
        },
        // Switch flips only after the swal (§1.1 step 3) and saves at once.
        async onToggle(next) {
            const text = this.$t(next ? "marketing.affiliate.settings.confirm_on" : "marketing.affiliate.settings.confirm_off");
            const yes = await this.confirmBox(text, { title: this.$t("marketing.affiliate.settings.enabled"), danger: !next });
            if (!yes) return;
            await this.save(next);
        },
        async save(enabledOverride) {
            const form = { ...this.form };
            if (typeof enabledOverride === "boolean") form.enabled = enabledOverride;
            this.errors = settingsErrors(form);
            if (Object.keys(this.errors).length) return;
            this.saving = true;
            try {
                this.applySettings(this.unwrap(await AffiliateService.saveSettings(settingsPayload(form, this.bumpTerms))) || {});
                this.notifyOk();
            } catch (err) {
                const code = apiErrorCode(err);
                if (code === "stale") this.stale = true;
                else if (code === "invalid_body") {
                    const list = (err.response.data && err.response.data.errors) || [];
                    const e = {};
                    list.forEach((x) => (e[x.field] = x.rule));
                    this.errors = e;
                    this.showError(err);
                } else this.showError(err);
            } finally {
                this.saving = false;
            }
        },
        openRule(row) {
            this.ruleEdit = row;
            this.ruleOpen = true;
        },
        async onRuleAction(cmd, row) {
            if (cmd === "edit") return this.openRule(row);
            try {
                if (cmd === "disable") await AffiliateService.disableRule(row.id);
                else if (cmd === "enable") await AffiliateService.enableRule(row.id);
                this.notifyOk();
                this.loadRules();
            } catch (err) {
                this.showError(err);
            }
        }
    }
};
</script>

<style scoped>
/* Verifier B: English headers broke mid-word (COMMISSIO/N) — the theme breaks
   anywhere; header cells wrap at spaces only. */
.aff-table >>> th .cell {
    word-break: normal;
    overflow-wrap: normal;
}

.aff-form {
    max-width: 960px;
}
</style>
